import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import CreateTripSheet from '../shared/CreateTripSheet';

const BottomBar = styled.nav`
  display: none;

  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
    padding: 0 0 env(safe-area-inset-bottom, 0px);
    align-items: flex-end;
    justify-content: space-around;
    height: calc(64px + env(safe-area-inset-bottom, 0px));
  }
`;

const NavItem = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  flex: 1;
  padding: 8px 4px 10px;
  text-decoration: none;
  color: ${props => props.$active ? '#1A73E8' : '#999'};
  transition: color 0.2s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;

  svg {
    width: 22px;
    height: 22px;
    stroke-width: ${props => props.$active ? 2.2 : 1.6};
    transition: stroke-width 0.2s ease;
  }

  &:active {
    transform: scale(0.9);
  }
`;

const NavLabel = styled.span`
  font-size: 10px;
  font-weight: ${props => props.$active ? 700 : 500};
  letter-spacing: 0.1px;
  line-height: 1;
`;

const pulseRing = keyframes`
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
  70% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const CreateButtonWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 10px;
  position: relative;
`;

const CreateButton = styled.button`
  width: 52px;
  height: 52px;
  border-radius: 18px;
  border: none;
  cursor: pointer;
  position: relative;
  bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1A73E8 0%, #4285F4 50%, #5E97F6 100%);
  background-size: 200% 200%;
  animation: ${shimmer} 3s ease infinite;
  box-shadow:
    0 4px 16px rgba(26, 115, 232, 0.4),
    0 1px 4px rgba(26, 115, 232, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 52px;
    height: 52px;
    border-radius: 18px;
    background: linear-gradient(135deg, #1A73E8, #4285F4);
    animation: ${pulseRing} 2.5s ease-out infinite;
    z-index: -1;
  }

  &:active {
    transform: scale(0.92);
    box-shadow:
      0 2px 8px rgba(26, 115, 232, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }

  svg {
    width: 26px;
    height: 26px;
    color: white;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
  }
`;

const CreateLabel = styled.span`
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 9px;
  font-weight: 700;
  color: #1A73E8;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />
    <path d="M9 21V12h6v9" />
  </svg>
);

const GuidesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
);

const TripsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);

const ProfileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M20 21a8 8 0 10-16 0" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const leftItems = [
  { path: '/', label: 'Home', icon: HomeIcon, exact: true },
  { path: '/guides', label: 'Guides', icon: GuidesIcon },
];

const rightItems = [
  { path: '/itineraries', label: 'Trips', icon: TripsIcon },
  { path: '/profile', label: 'Profile', icon: ProfileIcon },
];

function BottomNavigation() {
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const isItineraryDetail = /^\/itineraries\/.+/.test(location.pathname);
  if (isItineraryDetail) return null;

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const renderItem = (item) => {
    const active = isActive(item);
    const Icon = item.icon;
    return (
      <NavItem key={item.path} to={item.path} $active={active}>
        <Icon />
        <NavLabel $active={active}>{item.label}</NavLabel>
      </NavItem>
    );
  };

  return (
    <>
      <BottomBar aria-label="Mobile navigation">
        {leftItems.map(renderItem)}

        <CreateButtonWrapper>
          <CreateButton
            onClick={() => setSheetOpen(true)}
            aria-label="Create itinerary"
          >
            <PlusIcon />
          </CreateButton>
          <CreateLabel>Create</CreateLabel>
        </CreateButtonWrapper>

        {rightItems.map(renderItem)}
      </BottomBar>
      <CreateTripSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}

export default BottomNavigation;
